import { Link, useLocation } from "wouter";
import { getProPriceId } from "@/lib/stripe-price";
import { useUser } from "@clerk/react";
import {
  Check, PenLine, Zap, Mail,
  BarChart3, Brain, ShieldCheck, HeadphonesIcon, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSubscriptionTier } from "@/hooks/useSubscriptionTier";
import { useCreateCheckout, useOpenPortal } from "@/hooks/useStripe";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";

const PRO_FEATURES = [
  "Day, week & month diary",
  "Unlimited classes & students",
  "All record types — adjustments, behaviour, contact & general",
  "AI note polishing",
  "Full analytics & pattern insights",
  "All exports — PDF, CSV, Excel",
  "Student frequency & progress charts",
  "AI lesson prep suggestions",
  "Priority support",
];

export default function Pricing() {
  const [, setLocation] = useLocation();
  const { isSignedIn, user } = useUser();
  const { toast } = useToast();
  const { data: tierData } = useSubscriptionTier();
  const createCheckout = useCreateCheckout();
  const openPortal = useOpenPortal();

  const userTier = tierData?.tier ?? "free";
  // Use hasActiveSubscription so trial users see "Subscribe" not "Manage portal"
  const isSubscribed = (tierData as any)?.hasActiveSubscription === true;
  const betaAccessActive = (tierData as any)?.betaAccessActive === true;

  // Pro price ID is environment-driven (VITE_STRIPE_PRICE_ID). Dev, staging, and
  // prod each point at the right Stripe price without code changes; E2E uses
  // the test-mode price the same way.
  const priceId = getProPriceId();

  const handleSubscribe = async () => {
    if (!isSignedIn) { setLocation("/sign-up"); return; }
    try {
      const result = await createCheckout.mutateAsync(priceId);
      if (result.url) window.location.href = result.url;
    } catch (err) {
      toast({
        title: "Could not start checkout",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const handleManageBilling = async () => {
    try {
      const result = await openPortal.mutateAsync();
      if (result.url) window.location.href = result.url;
    } catch (err) {
      toast({
        title: "Could not open billing portal",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const handleSchoolEnquiry = () => {
    const userEmail = user?.primaryEmailAddress?.emailAddress ?? "";
    const userName = user ? [user.firstName, user.lastName].filter(Boolean).join(" ") : "";
    const ccParam = userEmail ? `&cc=${encodeURIComponent(userEmail)}` : "";
    const signOff = userName ? `Thanks,\n${userName}` : "Thanks,";
    const body = encodeURIComponent(
      `Hi ClassTrack team,\n\nI'm interested in learning more about ClassTrack for our school and would love to book a demo.\n\nSchool name:\nYour name & role:\nState/Territory or country:\n\n${signOff}`
    );
    window.location.href = `mailto:classtrack.com.au@gmail.com?subject=School%20Pack%20Enquiry%20%26%20Demo${ccParam}&body=${body}`;
  };

  return (
    <div className="min-h-[100dvh] bg-background">
      {/* Nav */}
      <header className="px-6 py-4 flex items-center justify-between border-b border-border bg-white">
        <Link href="/" className="flex items-center gap-2">
          <PenLine className="h-6 w-6 text-primary" />
          <span className="text-xl font-bold tracking-tight">ClassTrack</span>
        </Link>
        <div className="flex gap-3 items-center">
          {isSignedIn ? (
            <Button variant="outline" size="sm" onClick={() => setLocation("/app/dashboard")}>
              Go to app
            </Button>
          ) : (
            <>
              <Link href="/sign-in" className="text-sm font-medium text-muted-foreground hover:text-foreground">
                Sign in
              </Link>
              <Link href="/">
                <Button size="sm" variant="outline">Register your interest</Button>
              </Link>
            </>
          )}
        </div>
      </header>

      {/* Hero */}
      <div className="py-16 px-6 text-center bg-gradient-to-b from-accent/40 to-background">
        <Badge className="mb-4 bg-accent text-accent-foreground border-0 text-xs font-semibold px-3 py-1">
          Early access · Invite only
        </Badge>
        <h1 className="text-4xl font-bold text-card-foreground mb-3">
          ClassTrack Pro — what's included
        </h1>
        <p className="text-lg text-muted-foreground max-w-xl mx-auto">
          ClassTrack is currently open to invited teachers. If you've received an invitation, here's everything Pro includes.
        </p>
      </div>

      {/* Plans */}
      <div className="max-w-4xl mx-auto px-6 pb-20">
        <div className="max-w-md mx-auto">

          {/* Pro — single individual plan */}
          <div className="rounded-2xl border-2 border-primary bg-accent/15 p-8 relative flex flex-col">
            <div className="absolute -top-3 left-8">
              <span className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                <Zap className="h-3 w-3" /> Individual plan
              </span>
            </div>

            <div className="mb-6 mt-2">
              <h2 className="text-2xl font-bold text-card-foreground">Pro</h2>
              <p className="text-muted-foreground text-sm mt-1">Everything a teacher needs, in one place.</p>
            </div>

            <p className="text-sm text-muted-foreground mb-6">
              Pricing is confirmed at the time of your invitation.
            </p>

            {isSignedIn && betaAccessActive ? (
              <div className="w-full mb-6 p-4 rounded-xl border border-teal-200 bg-teal-50 text-center">
                <p className="text-sm font-semibold text-teal-800">You have founding beta access until 1 Jan 2027.</p>
                <p className="text-xs text-teal-700 mt-1">No payment is needed during beta.</p>
              </div>
            ) : isSignedIn && isSubscribed ? (
              <Button
                className="w-full mb-6"
                onClick={handleManageBilling}
                disabled={openPortal.isPending}
              >
                {openPortal.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Manage subscription
              </Button>
            ) : isSignedIn ? (
              <Button
                className="w-full mb-6"
                onClick={handleSubscribe}
                disabled={createCheckout.isPending}
              >
                {createCheckout.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Subscribe to Pro
              </Button>
            ) : (
              <Link href="/" className="mb-6">
                <Button className="w-full" variant="outline">Register your interest</Button>
              </Link>
            )}

            <ul className="space-y-3 flex-1">
              {PRO_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-foreground">
                  <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>

            {!betaAccessActive && (
              <p className="text-xs text-gray-400 mt-6 pt-6 border-t border-gray-100">
                Billed in AUD, inclusive of GST. Cancel anytime.
              </p>
            )}
          </div>

        </div>

        {/* Whole-school rollouts — non-tier CTA paragraph */}
        <div className="max-w-2xl mx-auto mt-10 px-6 py-6 rounded-2xl bg-secondary border border-border text-center">
          <p className="text-sm text-foreground leading-relaxed">
            <span className="font-semibold text-card-foreground">Rolling ClassTrack out across a whole school?</span>{" "}
            We can sort onboarding, leadership analytics, a data handling agreement, and invoice billing for your team.
            Pricing is tailored to your school size — get in touch for a quote and a walkthrough.
          </p>
          <Button onClick={handleSchoolEnquiry} variant="outline" className="mt-4">
            <Mail className="h-4 w-4 mr-2" />
            Talk to us about a school rollout
          </Button>
        </div>

        {/* Feature highlights row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
          {[
            { icon: Brain, label: "AI-powered", sub: "Smart note polishing & lesson prep" },
            { icon: BarChart3, label: "Analytics", sub: "Patterns across students & classes" },
            { icon: ShieldCheck, label: "Compliant", sub: "Built for audit-ready documentation" },
            { icon: HeadphonesIcon, label: "Supported", sub: "Priority help when you need it" },
          ].map(({ icon: Icon, label, sub }) => (
            <div key={label} className="text-center p-4 rounded-xl border border-border bg-card">
              <div className="bg-accent w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <p className="text-sm font-semibold text-card-foreground">{label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="mt-14">
          <h2 className="text-xl font-bold text-card-foreground mb-6 text-center">Common questions</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                q: "What happens after my trial?",
                a: "During the trial period, you can use ClassTrack before choosing whether to subscribe. If your trial ends without an active subscription, your account becomes read-only until you subscribe.",
              },
              {
                q: "Is this suitable for NZ and US teachers?",
                a: "Yes. While ClassTrack was built by an Australian teacher, the core features — diary, records, analytics, and AI polish — work for any classroom, anywhere.",
              },
              {
                q: "Do I need a credit card to start?",
                a: "No. Your trial starts the moment you create an account — no payment details required.",
              },
              {
                q: "Rolling ClassTrack out across a whole school?",
                a: "Talk to us. We'll set up Pro for every teacher on staff, plus leadership analytics, a data handling agreement, an onboarding session, and invoice billing. Pricing is tailored to your school size.",
              },
              {
                q: "Can I cancel anytime?",
                a: "Yes. Annual plans can be cancelled and you'll retain access until the end of your billing period. No lock-in contracts for individual teachers.",
              },
              {
                q: "What regions are your servers in?",
                a: "Your student records, classes, diary entries, and markbook scores are stored in our application database, which has been hosted in Australia (AWS Sydney, ap-southeast-2) since May 2026. The API server and uploaded files currently run on US-based infrastructure through Replit. Full details are available in our Privacy Policy and Subprocessors pages.",
              },
            ].map(({ q, a }) => (
              <div key={q} className="p-5 rounded-xl border border-border bg-secondary">
                <p className="font-semibold text-foreground text-sm mb-2">{q}</p>
                <p className="text-muted-foreground text-sm leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-10">
          By subscribing you agree to our{" "}
          <Link href="/terms" className="underline hover:text-foreground">Terms of Service</Link>
          {" "}and{" "}
          <Link href="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
        </p>
      </div>

      <footer className="bg-gray-900 text-gray-400 py-8 px-6 text-center text-sm">
        <div className="flex justify-center gap-6 mb-3">
          <Link href="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link href="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
        </div>
        <p>&copy; 2026 ClassTrack. All rights reserved.</p>
      </footer>
    </div>
  );
}
